import React, { useEffect, useState } from 'react';
import { Form, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { handleFormErrors } from '../../common/errorHelpers';
import { getFormFieldData } from '../../common/filterHelpers';
import { SearchFormFieldEnum } from '../../common/searchFormField.types';
import { StyledDivider } from '../../common/StyledDivider';
import { AttachementTypeEnum } from '../../common/types';
import { useAddCandidate } from '../../common/useAddCandidate';
import { useAddCandidateNote } from '../../common/useAddCandidateNote';
import { useCandidateAttachFile } from '../../common/useCandidateAttachFile';
import {
  CandidateAdditionalDataForm,
  CandidateDataForm,
  CandidateStartingNoteForm,
  mapErrorFieldsToFormLabels,
} from '../../components/CandidateDataForm';
import { DetailsPageHeader } from '../../components/DetailsPageHeader';
import { StyledColumn } from '../../components/StyledColumn';
import { StyledRow } from '../../components/StyledRow';
import { CandidatesPersonalData } from '../candidate-details/CandidatesPersonalData';
import { CandidateType } from '../candidates/Candidates.types';
import { EmailData } from '../email-details/EmailData';
import { useGetEmail } from '../email-details/useGetEmail';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const FormColumn = styled(StyledColumn)`
  flex-grow: 1;
  border-color: #bdbdbd;
`;

type UploadedEmailFile = {
  uid: string;
  response: {
    uuid: string;
    name: string;
  };
  lastModified: number;
};

export const mapFileToAttachment = (file: UploadedEmailFile) => {
  return {
    id: file?.uid,
    fileId: file?.response.uuid,
    name: file.response.name,
    createdDate: file.lastModified,
  };
};

export const EmailConvertPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: email } = useGetEmail(id || '');
  const addCandidate = useAddCandidate();
  const attachFile = useCandidateAttachFile();
  const addNote = useAddCandidateNote();

  const [cvAttachement, setCvAttachement] = useState<string | undefined>();

  useEffect(() => {
    form.setFieldValue('email', email?.from);
    form.setFieldValue('note', email?.body);
  }, [form, email?.from, email?.body]);

  if (!email) return <></>;

  const { sentDate, from, subject, body, attachments } = email;

  const handleSave = async () => {
    const { note, ...dataValues } = form.getFieldsValue();

    const formValuesToPayload = {
      ...dataValues,
      [SearchFormFieldEnum.ContractTypes]:
        getFormFieldData(SearchFormFieldEnum.ContractTypes).toPayloadValueFormatter?.(
          dataValues[SearchFormFieldEnum.ContractTypes]
        ) ?? [],
      cv: cvAttachement,
    };

    const newCandidate = await addCandidate.mutateAsync(
      { data: formValuesToPayload },
      {
        onSuccess: (record) => {
          if (note) {
            addNote.mutate({ queryParams: { id: record.data.uuid }, data: { content: note } });
          }
        },
        onError: (error) => {
          handleFormErrors({ error, form, mapErrorFieldsToFormLabels });
        },
      }
    );

    email.attachments.forEach((attachment) => {
      attachFile.mutate({
        queryParams: { id: (newCandidate.data as unknown as CandidateType).uuid ?? '' },
        data: { fileId: attachment.fileId, type: AttachementTypeEnum.OTHER },
      });
    });
  };

  const handleCancel = () => navigate(-1);

  return (
    <MainContainer>
      <DetailsPageHeader title="Convert email to candidate" onSave={handleSave} onCancel={handleCancel} />

      <Typography.Title level={4}>Email data</Typography.Title>
      <EmailData
        sentDate={sentDate}
        from={from}
        subject={subject}
        body={body}
        attachments={attachments}
        handleAttachmentDragging={setCvAttachement}
      />

      <StyledDivider />

      <Typography.Title level={4}>Candidate data</Typography.Title>
      <StyledRow gap="0.86rem">
        <FormColumn>
          <CandidateDataForm form={form}></CandidateDataForm>
          <CandidateAdditionalDataForm form={form} />
          <CandidateStartingNoteForm form={form} />
        </FormColumn>

        <CandidatesPersonalData cvAttachement={cvAttachement} addCvAttachement={setCvAttachement} height={1000} />
      </StyledRow>
    </MainContainer>
  );
};
